import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ColumnPinningState, ColumnOrderState } from '@tanstack/react-table'
import { TableQueryState } from '../utils/rison-helpers'

export enum sortDirection {
    AZ = "az",
    ZA = "za",
    NEWEST = "newest",
    OLDEST = "oldest",
    NONE = "none",
}

// Type for custom sort configuration per column
// Maps sortDirection enum values to actual API parameter strings
export type ColumnSortConfig = {
    allowedDirections: Partial<Record<sortDirection, string>>
    default: sortDirection
}

// Map of column id to its sort configuration
export type ColumnSortConfigs = Record<string, ColumnSortConfig>

// Row selection state type
export interface RowSelectionState {
    all: boolean
    only: string[]
    except: string[]
}

// State for a single table instance
export interface SingleTableState {
    // Table query state
    page: number
    pageSize: number
    sort: Array<{ id: string; parameter: sortDirection }>
    filters: Record<string, any>
    search: string
    columnSizes: Record<string, number>
    columnPinned: ColumnPinningState
    columnVisibility: Record<string, boolean>
    columnOrder: ColumnOrderState
    temporaryColumnOrder?: ColumnOrderState | undefined
    selectedRowIds: string[]
    loading: boolean
    validating: boolean
    columnRePositioning: boolean
    total: number
    fetcherOpts: any
    uriQueryPrefix: string
    // Column sort configurations for custom API parameter mappings
    columnSortConfigs: ColumnSortConfigs
}

export interface TableState {
    tables: {
        [slug: string]: SingleTableState
    }
}

const createInitialTableState = (slug: string, initialState?: Partial<TableQueryState>): SingleTableState => ({
    // Table query state
    page: initialState?.page ?? 0,
    pageSize: initialState?.pageSize ?? 100, // Default to 100
    sort: initialState?.sort ?? [],
    filters: initialState?.filters ?? {},
    search: initialState?.search ?? '',
    columnSizes: initialState?.columns?.sizes ?? {},
    columnPinned: initialState?.columns?.pinned ?? { left: [], right: [] },
    columnVisibility: initialState?.columns?.visibility ?? {},
    columnOrder: [],
    temporaryColumnOrder: undefined,
    selectedRowIds: initialState?.selectedRowIds ?? [],
    loading: false,
    validating: false,
    columnRePositioning: false,
    total: 0,
    fetcherOpts: null,
    uriQueryPrefix: slug,
    columnSortConfigs: {},
})

const initialState: TableState = {
    tables: {},
}

const tableSlice = createSlice({
    name: 'table',
    initialState,
    reducers: {

        initializeTable: (
            state,
            action: PayloadAction<{ slug: string; initialState?: Partial<TableQueryState> }>
        ) => {
            const { slug, initialState: initial } = action.payload
            if (!state.tables[slug]) {
                state.tables[slug] = createInitialTableState(slug, initial)
            }
        },

        removeTable: (state, action: PayloadAction<string>) => {
            delete state.tables[action.payload]
        },

        setPage: (state, action: PayloadAction<{ slug: string; page: number }>) => {
            const { slug, page } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].page = page
            }
        },

        setPageSize: (state, action: PayloadAction<{ slug: string; pageSize: number }>) => {
            const { slug, pageSize } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].pageSize = pageSize
                state.tables[slug].page = 0 // Reset to first page
            }
        },

        setSort: (state, action: PayloadAction<{ slug: string; sort: Array<{ id: string; parameter: sortDirection }> }>) => {
            const { slug, sort } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].sort = sort
            }
        },

        setSingleColumnSort: (
            state,
            action: PayloadAction<{ slug: string; column: string; parameter: sortDirection }>
        ) => {
            const { slug, column, parameter } = action.payload
            if (state.tables[slug]) {
                const newSort = [{ id: column, parameter }]
                state.tables[slug].sort = newSort

            }
        },

        mutateColumnSort: (
            state,
            action: PayloadAction<{ slug: string; column: string; parameter: sortDirection }>
        ) => {
            const { slug, column, parameter } = action.payload
            if (state.tables[slug]) {
                const existingSort = state.tables[slug].sort.filter(s => s.id !== column)
                state.tables[slug].sort = [...existingSort, { id: column, parameter }]

            }
        },

        removeColumnSort: (state, action: PayloadAction<{ slug: string; column: string }>) => {
            const { slug, column } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].sort = state.tables[slug].sort.filter(s => s.id !== column)
            }
        },

        resetColumnSort: (state, action: PayloadAction<string>) => {
            const slug = action.payload
            if (state.tables[slug]) {
                state.tables[slug].sort = []
            }
        },

        setFilters: (state, action: PayloadAction<{ slug: string; filters: Record<string, any> }>) => {
            const { slug, filters } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].filters = filters
                state.tables[slug].page = 0 // Reset to first page
            }
        },

        updateFilter: (state, action: PayloadAction<{ slug: string; key: string; value: any }>) => {
            const { slug, key, value } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].filters[key] = value
                state.tables[slug].page = 0
            }
        },

        removeFilter: (state, action: PayloadAction<{ slug: string; key: string }>) => {
            const { slug, key } = action.payload
            if (state.tables[slug]) {
                delete state.tables[slug].filters[key]
                state.tables[slug].page = 0
            }
        },

        clearFilters: (state, action: PayloadAction<string>) => {
            const slug = action.payload
            if (state.tables[slug]) {
                state.tables[slug].filters = {}
                state.tables[slug].page = 0
            }
        },

        setSearch: (state, action: PayloadAction<{ slug: string; search: string }>) => {
            const { slug, search } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].search = search
                state.tables[slug].page = 0 // Reset to first page
            }
        },

        setColumnSizes: (state, action: PayloadAction<{ slug: string; sizes: Record<string, number> }>) => {
            const { slug, sizes } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnSizes = sizes
            }
        },

        setColumnPinned: (
            state,
            action: PayloadAction<{ slug: string; pinned: ColumnPinningState }>
        ) => {
            const { slug, pinned } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnPinned = pinned
            }
        },

        setColumnOrder: (state, action: PayloadAction<{ slug: string; order: ColumnOrderState }>) => {
            const { slug, order } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnOrder = order
            }
        },

        setTemporaryColumnOrder: (state, action: PayloadAction<{ slug: string; order: ColumnOrderState | undefined }>) => {
            const { slug, order } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].temporaryColumnOrder = order
            }
        },


        setColumnVisibility: (state, action: PayloadAction<{ slug: string; visibility: Record<string, boolean> }>) => {
            const { slug, visibility } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnVisibility = visibility
            }
        },

        setSelectedRowIds: (state, action: PayloadAction<{ slug: string; ids: string[] }>) => {
            const { slug, ids } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].selectedRowIds = ids
            }
        },
        resetRowSelection: (state, action: PayloadAction<string>) => {
            const slug = action.payload
            if (state.tables[slug]) {
                state.tables[slug].selectedRowIds = []
            }
        },

        setLoading: (state, action: PayloadAction<{ slug: string; loading: boolean }>) => {
            const { slug, loading } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].loading = loading
            }
        },

        setValidating: (state, action: PayloadAction<{ slug: string; validating: boolean }>) => {
            const { slug, validating } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].validating = validating
            }
        },

        setColumnRePositioning: (state, action: PayloadAction<{ slug: string; positioning: boolean }>) => {
            const { slug, positioning } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnRePositioning = positioning
            }
        },

        setTotal: (state, action: PayloadAction<{ slug: string; total: number }>) => {
            const { slug, total } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].total = total
            }
        },

        setFetcherOpts: (state, action: PayloadAction<{ slug: string; opts: any }>) => {
            const { slug, opts } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].fetcherOpts = opts
            }
        },

        setColumnSortConfigs: (state, action: PayloadAction<{ slug: string; configs: ColumnSortConfigs }>) => {
            const { slug, configs } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnSortConfigs = configs
            }
        },

        updateTableState: (state, action: PayloadAction<{ slug: string; updates: Partial<TableQueryState> }>) => {
            const { slug, updates } = action.payload
            if (state.tables[slug]) {
                if (updates.page !== undefined) state.tables[slug].page = updates.page
                if (updates.pageSize !== undefined) state.tables[slug].pageSize = updates.pageSize
                if (updates.sort !== undefined) {
                    state.tables[slug].sort = updates.sort

                }
                if (updates.filters !== undefined) state.tables[slug].filters = updates.filters
                if (updates.search !== undefined) state.tables[slug].search = updates.search
                if (updates.columns?.sizes !== undefined) state.tables[slug].columnSizes = updates.columns.sizes
                if (updates.columns?.pinned !== undefined) {
                    state.tables[slug].columnPinned = updates.columns.pinned as ColumnPinningState
                }
                if (updates.columns?.visibility !== undefined) state.tables[slug].columnVisibility = updates.columns.visibility
                if (updates.selectedRowIds !== undefined) {
                    state.tables[slug].selectedRowIds = updates.selectedRowIds
                }
            }
        },

        resetTableState: (state, action: PayloadAction<{ slug: string; initialState?: Partial<TableQueryState> }>) => {
            const { slug, initialState: initial } = action.payload
            if (state.tables[slug]) {
                state.tables[slug] = createInitialTableState(slug, initial)
            }
        },
    },
})

// Export actions
export const {
    initializeTable,
    removeTable,
    setPage,
    setPageSize,
    setSort,
    setSingleColumnSort,
    mutateColumnSort,
    removeColumnSort,
    resetColumnSort,
    setFilters,
    updateFilter,
    removeFilter,
    clearFilters,
    setSearch,
    setColumnSizes,
    setColumnPinned,
    setColumnOrder,
    setTemporaryColumnOrder,
    setColumnVisibility,
    setSelectedRowIds,
    resetRowSelection,
    setLoading,
    setValidating,
    setColumnRePositioning,
    setTotal,
    setFetcherOpts,
    setColumnSortConfigs,
    updateTableState,
    resetTableState,
} = tableSlice.actions

export const selectTableState = (state: { table: TableState }, slug: string) => state.table.tables[slug]
export const selectTableExists = (state: { table: TableState }, slug: string) => !!state.table.tables[slug]

export default tableSlice.reducer
